<script setup lang="ts">
import { ref, Ref } from 'vue';
import useModal, { ModalTypes } from '../../composables/useModal';
import Modal from '../Modal.vue';
import CreateMultipleUsers from './CreateMultipleUsers.vue';
import CreateMultipleLoginCards from '../LoginCard/CreateMultipleLoginCards.vue';
import Confirmation from '../Confirmation/index.vue';
import { CreatedUser } from './useCreateMultipleUsers';
import { useLocale } from '../../composables/useLocale';
import { computed } from 'vue';

const { t } = useLocale();

type Props = {
  name: ModalTypes;
};
const props = defineProps<Props>();

const createdUsers: Ref<CreatedUser[]> = ref([]);
const onUsersCreated = (users: CreatedUser[]) => {
  createdUsers.value = users;
  if (users.length === 0) {
    close();
  }
};

const createdNonEmployee = computed(() => {
  return createdUsers.value.filter((u) => ['student', 'guardian'].includes(u.role));
});

const { isOpen, close } = useModal(props.name);
// remind users to print cards before closing
const cardsPrinted = ref(true);
const confirmAndClose = () => {
  isDownloading.value = false;
  if (createdNonEmployee.value.length > 0 || cardsPrinted.value) {
    close();
  }
};

const createMoreUsers = () => {
  createdUsers.value = [];
};

const cards = ref();
const isDownloading = ref(false);
const downloadLoginCards = async () => {
  if (isDownloading.value) return;
  isDownloading.value = true;
  await cards.value
    .downloadLoginCard()
    .then(() => {
      isDownloading.value = false;
      cardsPrinted.value = true;
    })
    .catch(() => {
      isDownloading.value = false;
    });

  isDownloading.value = false;
};

const isConfirmationPage = computed(() => createdUsers.value.length > 0);
</script>

<template>
  <Modal
    :name="name"
    :title="
      isConfirmationPage
        ? t('lcp.new_users_created', { count: createdUsers.length })
        : t('lcp.create_multiple_new_users')
    "
    title-class="text-h5"
    :is-open="isOpen"
    :show-close-btn="false"
    :show-cancel-action="false"
    :top-left-btn-action="isConfirmationPage ? undefined : confirmAndClose"
    :top-left-btn-label="t('lcp.close')"
    top-left-btn-icon="mdi-close"
    fullscreen
    contained
    background-color="rgba(var(--v-theme-surface-container))"
    @afterLeave="createdUsers = []"
  >
    <v-expand-transition mode="out-in">
      <CreateMultipleUsers v-if="!isConfirmationPage" @usersCreated="onUsersCreated" />
    </v-expand-transition>
    <v-expand-transition mode="out-in">
      <Confirmation
        v-if="isConfirmationPage"
        @done-click="confirmAndClose"
        @create-more-users-click="createMoreUsers"
        @download-login-cards-click="downloadLoginCards"
        :is-downloading-login-cards="isDownloading"
        show-support-card
        :only-employee-created-mode="!createdNonEmployee.length"
      />
    </v-expand-transition>
    <div style="display: none">
      <CreateMultipleLoginCards ref="cards" :users="createdNonEmployee" />
    </div>
  </Modal>
</template>
